/*!
 * AYLINAS · Universal Multilingual Audio Playlist
 * Vanilla JS custom element <aylinas-audio-playlist>. One <audio> element per
 * instance, no dependencies, no audio preloading (metadata-only duration probe,
 * throttled). Multiple instances on one page never play at the same time.
 * Reads data rendered by sections/aylinas-audio-playlist.liquid — never writes to Shopify.
 */
(() => {
  if (window.customElements && customElements.get('aylinas-audio-playlist')) return;

  const LANGS = ['de', 'en', 'ar'];
  const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
  const PREF_KEY = 'aylinas-playlist:prefs:v1';
  const FAV_KEY = 'aylinas-playlist:fav:';
  const DUR_KEY = 'aylinas-playlist:dur:v1';
  const MODE_PRESETS = {
    continuous: { plays: 1, repGap: 1, trackGap: 1 },
    repeat: { plays: 3, repGap: 1, trackGap: 2 },
    listen: { plays: 1, repGap: 1, trackGap: 3 },
    practice: { plays: 2, repGap: 3, trackGap: 2 }
  };

  /* ---------- small utilities ---------- */
  const store = {
    get(k, fb) { try { const v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch (e) { return fb; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } },
    del(k) { try { localStorage.removeItem(k); } catch (e) { /* storage unavailable */ } },
    sget(k, fb) { try { const v = sessionStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch (e) { return fb; } },
    sset(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* ignore */ } }
  };
  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60), r = Math.floor(s % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
  };
  const fmtTotal = (s) => {
    if (s < 60) return Math.max(1, Math.round(s)) + ' s';
    const m = Math.round(s / 60);
    return m < 60 ? m + ' min' : Math.floor(m / 60) + ' h ' + (m % 60) + ' min';
  };
  /* Unicode-aware search normaliser: case, Latin diacritics (ä→a and ä→ae),
     ß→ss, Arabic tashkeel/tatweel, alef/ya/ta-marbuta variants. */
  const normLatin = (s) => s.toLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[̀-ͯ]/g, '');
  const normDE = (s) => s.toLowerCase().replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue');
  const normAR = (s) => s.replace(/[ً-ٰٟـ]/g, '').replace(/[أإآٱ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه').replace(/ؤ/g, 'و').replace(/ئ/g, 'ي');
  const norm = (s) => normAR(normLatin(s || '')).replace(/\s+/g, ' ').trim();
  const shuffleArr = (a) => { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
  const escapeHTML = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const articleHTML = (de, on) => {
    if (!de) return '';
    const m = on && /^(der|die|das)\s+(.+)$/.exec(de);
    return m ? '<span class="ayp-art ayp-art--' + m[1] + '">' + m[1] + '</span> ' + escapeHTML(m[2]) : escapeHTML(de);
  };
  const IS_IOS_VOLUME_LOCKED = (() => {
    try { const a = document.createElement('audio'); a.volume = 0.5; return a.volume !== 0.5; } catch (e) { return true; }
  })();

  /* Global coordinator: only one instance plays at a time. */
  const instances = new Set();

  /* Shared, throttled duration probe (metadata only, cached per session). */
  const durCache = store.sget(DUR_KEY, {});
  const probeQueue = [];
  let probing = 0;
  const probeNext = () => {
    while (probing < 3 && probeQueue.length) {
      const { url, cb } = probeQueue.shift();
      if (durCache[url] != null) { cb(durCache[url]); continue; }
      probing++;
      const a = document.createElement('audio');
      a.preload = 'metadata';
      let done = false;
      const finish = (d) => {
        if (done) return; done = true; probing--;
        a.removeAttribute('src'); try { a.load(); } catch (e) { /* noop */ }
        if (d != null && isFinite(d)) { durCache[url] = d; store.sset(DUR_KEY, durCache); }
        cb(d); probeNext();
      };
      a.addEventListener('loadedmetadata', () => finish(a.duration), { once: true });
      a.addEventListener('error', () => finish(null), { once: true });
      setTimeout(() => finish(null), 15000);
      a.src = url;
    }
  };
  const probe = (url, cb) => {
    if (durCache[url] != null) { cb(durCache[url]); return; }
    probeQueue.push({ url, cb }); probeNext();
  };

  class AylinasPlaylist extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      const cfgEl = this.querySelector('script[data-ayp-config]');
      const trEl = this.querySelector('script[data-ayp-tracks]');
      if (!cfgEl || !trEl) return; // empty state — nothing to wire
      try {
        this.cfg = JSON.parse(cfgEl.textContent);
        this.tracks = JSON.parse(trEl.textContent) || [];
      } catch (e) { console.error('[AYLINAS playlist] invalid data', e); return; }
      this._ready = true;
      this._ac = new AbortController();
      this._timers = new Set();
      instances.add(this);
      this.init();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      instances.delete(this);
      this.clearTimers();
      if (this._ac) this._ac.abort();
      if (this.audio) { this.audio.pause(); this.audio.removeAttribute('src'); try { this.audio.load(); } catch (e) { /* noop */ } }
      if (this._io) this._io.disconnect();
      document.documentElement.classList.remove('ayp-lock');
    }

    log(...a) { if (this.cfg && this.cfg.debug) console.log('[AYLINAS playlist]', ...a); }

    /* ================= init ================= */
    init() {
      const c = this.cfg;
      const $ = (s) => this.querySelector(s);
      this.$ = $;
      this.ui = {
        list: $('[data-list]'), rows: Array.from(this.querySelectorAll('.ayp-row')),
        np: $('[data-np]'), npImg: $('[data-np-img]'), npPh: $('[data-np-ph]'), npWords: $('[data-np-words]'), npPos: $('[data-np-pos]'),
        status: $('[data-status]'), seek: $('[data-seek]'), cur: $('[data-cur]'), durTotal: $('[data-dur-total]'),
        seg: $('[data-audio-langs]'), mini: $('[data-mini]'), miniImg: $('[data-mini-img]'), miniWord: $('[data-mini-word]'),
        miniSub: $('[data-mini-sub]'), miniBar: $('[data-mini-progress]'), search: $('[data-search]'),
        clear: $('[data-action="clear-search"]'), filter: $('[data-filter]'), noRes: $('[data-noresults]'),
        dialog: $('[data-settings]'), audioChoices: $('[data-audio-choices]'), orderList: $('[data-order-list]'),
        volume: $('[data-volume]'), volWrap: $('[data-volume-wrap]'), total: $('[data-total]'), totalText: $('[data-total-text]'),
        speedLbl: $('[data-speed-label]'), editorStats: $('[data-editor-stats]')
      };
      this.rowById = new Map();
      this.ui.rows.forEach((r) => this.rowById.set(+r.dataset.i, r));

      /* search index */
      this.tracks.forEach((t, i) => {
        t.i = i;
        const parts = [t.de, t.en, t.ar].filter(Boolean);
        t._q = norm(parts.join(' ')) + ' ' + norm(normDE(t.de || ''));
      });

      /* optional "sort_order" ordering — reorder DOM once */
      if (c.trackOrder === 'sort' && this.tracks.some((t) => t.so != null)) {
        const sorted = this.tracks.slice().sort((a, b) => (a.so ?? 1e9) - (b.so ?? 1e9) || a.i - b.i);
        const frag = document.createDocumentFragment();
        sorted.forEach((t, pos) => {
          const r = this.rowById.get(t.i);
          if (!r) return;
          const n = r.querySelector('.ayp-row__n'); if (n) n.textContent = pos + 1;
          frag.appendChild(r);
        });
        this.ui.list.appendChild(frag);
        this.baseOrder = sorted.map((t) => t.i);
      } else {
        this.baseOrder = this.tracks.map((t) => t.i);
      }

      /* audio languages that actually have files */
      this.audioLangs = LANGS.filter((l) => this.tracks.some((t) => t.a && t.a[l]));

      /* preferences: merchant defaults ← visitor overrides */
      this.defaults = {
        audioLang: c.audioLang, speed: parseFloat(c.speed) || 1, repeat: c.repeat || 'off', shuffle: !!c.shuffle,
        autoNext: c.autoNext !== false, mode: c.mode || 'continuous', plays: +c.plays || 1,
        repGap: +c.repGap || 0, trackGap: c.trackGap == null ? 1 : +c.trackGap,
        order: (c.order || LANGS).slice(), hidden: (c.hidden || []).filter(Boolean), remember: !!c.remember
      };
      const saved = c.remember ? store.get(PREF_KEY, null) : null;
      this.touched = new Set();
      this.p = Object.assign({}, this.defaults, { order: this.defaults.order.slice(), hidden: this.defaults.hidden.slice() }, saved && saved.remember !== false ? saved : {});
      if (saved && saved.remember === false) this.p.remember = false;
      if (!c.allowOrder) { this.p.order = this.defaults.order.slice(); this.p.hidden = this.defaults.hidden.slice(); }
      this.sanitizePrefs();

      this.favs = new Set(c.favorites ? store.get(FAV_KEY + c.playlistId, []) : []);

      /* state */
      this.cur = -1; this.queue = []; this.qPos = -1; this.playCount = 0; this.run = false;
      this.state = 'idle'; this.showRemaining = false; this.filterText = ''; this.filterCat = 'all';

      this.audio = document.createElement('audio');
      this.audio.preload = 'none';
      this.audio.setAttribute('playsinline', '');
      this.audio.crossOrigin = null;
      this.bindAudio();
      this.bindUI();
      this.buildCategories();
      this.applyLanguages();
      this.renderAudioLangs();
      this.renderOrderList();
      this.syncPrefControls();
      this.applyRowAvailability();
      this.applyFavs();
      if (c.loadDurations) this.loadDurations();
      if (this.ui.volWrap && IS_IOS_VOLUME_LOCKED) this.ui.volWrap.setAttribute('data-unsupported', '');
      if (this.ui.editorStats) {
        this.ui.editorStats.textContent = c.n + ' tracks · ' + c.nAudio + ' with audio (' + (this.audioLangs.join(', ') || 'none') + ')' + (c.n - c.nAudio ? ' · ' + (c.n - c.nAudio) + ' still need audio' : '');
      }
      this.setState('idle');
      this.updatePlayAllAvailability();
      this.log('ready', { tracks: this.tracks.length, audioLangs: this.audioLangs, prefs: this.p });
    }

    sanitizePrefs() {
      const p = this.p;
      if (!SPEEDS.includes(+p.speed)) p.speed = 1;
      if (!['off', 'all', 'one'].includes(p.repeat)) p.repeat = 'off';
      if (!MODE_PRESETS[p.mode]) p.mode = 'continuous';
      p.plays = Math.min(5, Math.max(1, Math.round(+p.plays || 1)));
      p.repGap = Math.min(10, Math.max(0, +p.repGap || 0));
      p.trackGap = Math.min(10, Math.max(0, +p.trackGap || 0));
      const ord = Array.isArray(p.order) ? p.order.filter((l) => LANGS.includes(l)) : [];
      LANGS.forEach((l) => { if (!ord.includes(l)) ord.push(l); });
      p.order = Array.from(new Set(ord));
      p.hidden = Array.isArray(p.hidden) ? p.hidden.filter((l) => LANGS.includes(l)) : [];
      if (p.hidden.includes(p.order[0])) p.hidden = p.hidden.filter((l) => l !== p.order[0]);
      if (!this.audioLangs.includes(p.audioLang)) p.audioLang = this.audioLangs.includes(this.defaults.audioLang) ? this.defaults.audioLang : (this.audioLangs[0] || this.defaults.audioLang);
    }

    savePrefs(keys) {
      if (!this.cfg.remember) return;
      (keys || []).forEach((k) => this.touched.add(k));
      if (!this.p.remember) { store.set(PREF_KEY, { remember: false }); return; }
      /* Only store what the visitor actually changed, so one page's merchant
         defaults never override another page's settings. */
      const out = Object.assign({}, store.get(PREF_KEY, {}) || {}, { remember: true });
      this.touched.forEach((k) => { out[k] = Array.isArray(this.p[k]) ? this.p[k].slice() : this.p[k]; });
      store.set(PREF_KEY, out);
    }

    /* ================= audio ================= */
    bindAudio() {
      const a = this.audio, sig = { signal: this._ac.signal };
      a.addEventListener('loadstart', () => { if (this.state !== 'playing') this.setState('loading'); }, sig);
      a.addEventListener('waiting', () => this.setState('buffering'), sig);
      a.addEventListener('playing', () => { this.setState('playing'); this.setStatus(''); }, sig);
      a.addEventListener('pause', () => { if (!a.ended && this.state !== 'waiting' && this.state !== 'error' && this.state !== 'noaudio') this.setState(this.cur >= 0 ? 'paused' : 'idle'); }, sig);
      a.addEventListener('loadedmetadata', () => {
        a.playbackRate = this.p.speed;
        const t = this.tracks[this.cur];
        if (t && isFinite(a.duration)) {
          const url = this.urlFor(t);
          if (url) durCache[url] = a.duration;
          this.setRowDuration(t.i, a.duration);
        }
        this.updateProgress();
      }, sig);
      a.addEventListener('timeupdate', () => this.updateProgress(), sig);
      a.addEventListener('progress', () => this.updateBuffered(), sig);
      a.addEventListener('ended', () => this.onEnded(), sig);
      a.addEventListener('error', () => {
        if (!a.getAttribute('src')) return;
        this.log('audio error', a.error);
        this.setState('error');
        this.setStatus(this.cfg.msgs.error, true);
        if (this.run && this.p.autoNext) this.later(() => this.advance(true), 1800);
      }, sig);
    }

    urlFor(t, lang) {
      if (!t || !t.a) return null;
      const l = lang || this.p.audioLang;
      return t.a[l] || (this.cfg.audioFallback ? t.a[this.defaults.audioLang] || this.audioLangs.map((x) => t.a[x]).find(Boolean) : null) || null;
    }
    playable(i) { return !!this.urlFor(this.tracks[i]); }

    clearTimers() { this._timers.forEach((t) => clearTimeout(t)); this._timers.clear(); }
    later(fn, ms) {
      const id = setTimeout(() => { this._timers.delete(id); fn(); }, ms);
      this._timers.add(id);
      return id;
    }

    pauseOthers() { instances.forEach((inst) => { if (inst !== this) inst.pause(true); }); }

    /* Visible (filtered) playable tracks, in display order */
    visibleOrder() {
      return this.baseOrder.filter((i) => !this.isFiltered(i));
    }

    buildQueue(startIndex, opts = {}) {
      let q = this.visibleOrder().filter((i) => this.playable(i));
      if (!q.length) q = this.baseOrder.filter((i) => this.playable(i));
      if (this.p.shuffle || opts.shuffle) {
        const rest = q.filter((i) => i !== startIndex);
        shuffleArr(rest);
        q = startIndex != null && this.playable(startIndex) ? [startIndex].concat(rest) : rest;
      }
      this.queue = q;
      this.qPos = startIndex != null ? Math.max(0, q.indexOf(startIndex)) : 0;
      this.log('queue', q);
    }

    /* Select a track (show it) and optionally play it */
    select(i, play) {
      const t = this.tracks[i];
      if (!t) return;
      this.clearTimers();
      const changed = i !== this.cur;
      this.cur = i;
      this.playCount = 0;
      this.renderNowPlaying();
      this.markRows();
      const url = this.urlFor(t);
      if (!url) {
        this.audio.pause();
        this.audio.removeAttribute('src');
        this.setState('noaudio');
        this.setStatus(this.cfg.msgs.noAudioWord);
        this.resetProgress(true);
        return;
      }
      if (changed || this.audio.getAttribute('src') !== url) {
        this.audio.src = url;
        this.audio.playbackRate = this.p.speed;
        this.resetProgress(false);
      }
      if (play) this.doPlay();
      else this.setState('paused');
    }

    doPlay() {
      if (this.cur < 0 || !this.playable(this.cur)) return;
      this.pauseOthers();
      this.clearTimers();
      this.audio.playbackRate = this.p.speed;
      this.audio.loop = false;
      this.setState(this.audio.readyState >= 3 ? 'playing' : 'loading');
      const pr = this.audio.play();
      if (pr && pr.catch) {
        pr.catch((err) => {
          if (err && err.name === 'AbortError') return; // superseded by a newer load
          this.log('play() rejected', err);
          this.setState('paused');
          if (err && err.name === 'NotAllowedError') this.setStatus('Tap play to start audio.');
          else { this.setState('error'); this.setStatus(this.cfg.msgs.error, true); }
        });
      }
      this.updateMediaSession();
    }

    pause(fromOther) {
      if (!this.audio) return;
      this.clearTimers();
      if (!this.audio.paused) this.audio.pause();
      if (this.cur >= 0 && this.state !== 'noaudio') this.setState('paused');
      if (fromOther) this.run = false;
      this.setRun();
    }

    toggle() {
      if (this.state === 'playing' || this.state === 'waiting' || this.state === 'loading' || this.state === 'buffering') { this.pause(); return; }
      if (this.cur < 0 || !this.playable(this.cur)) { this.playAll(false); return; }
      if (!this.queue.length || !this.queue.includes(this.cur)) this.buildQueue(this.cur);
      this.run = true; this.setRun();
      if (this.audio.ended || (this.audio.duration && this.audio.currentTime >= this.audio.duration - 0.05)) this.audio.currentTime = 0;
      this.doPlay();
    }

    stop() {
      this.clearTimers();
      this.run = false; this.setRun();
      this.audio.pause();
      try { this.audio.currentTime = 0; } catch (e) { /* not loaded */ }
      this.playCount = 0;
      this.setState(this.cur >= 0 ? 'paused' : 'idle');
      this.setStatus('');
      this.updateProgress();
    }

    playAll(shuffle) {
      if (shuffle) { this.p.shuffle = true; this.savePrefs(['shuffle']); this.syncPrefControls(); }
      this.buildQueue(null, { shuffle });
      if (!this.queue.length) { this.setStatus(this.cfg.msgs.noAudioWord); return; }
      this.run = true; this.setRun();
      this.qPos = 0;
      this.select(this.queue[0], true);
    }

    playIndex(i) {
      if (!this.playable(i)) { this.select(i, false); this.run = false; this.setRun(); return; }
      if (i === this.cur && this.audio.getAttribute('src')) { this.toggle(); return; }
      this.buildQueue(i);
      this.run = true; this.setRun();
      this.select(i, true);
    }

    onEnded() {
      this.updateMediaSession();
      if (this.p.repeat === 'one') { this.audio.currentTime = 0; this.doPlay(); return; }
      this.playCount++;
      if (this.playCount < this.p.plays) {
        this.setState('waiting');
        const gap = this.p.repGap;
        if (gap > 0) this.setStatus(this.p.mode === 'practice' || this.p.mode === 'listen' ? this.cfg.msgs.yourTurn : 'Again in ' + gap + ' s');
        this.later(() => { this.setStatus(''); this.audio.currentTime = 0; this.doPlay(); }, gap * 1000);
        return;
      }
      if (!this.run || !this.p.autoNext) {
        this.playCount = 0;
        this.setState('paused');
        this.run = false; this.setRun();
        return;
      }
      const gap = this.p.trackGap;
      const nextExists = this.peekNext() != null;
      if (!nextExists) { this.advance(true); return; }
      if (gap > 0) {
        this.setState('waiting');
        this.setStatus(this.p.mode === 'listen' || this.p.mode === 'practice' ? this.cfg.msgs.yourTurn : 'Next word in ' + gap + ' s');
        this.later(() => { this.setStatus(''); this.advance(true); }, gap * 1000);
      } else {
        this.advance(true);
      }
    }

    peekNext() {
      if (!this.queue.length) return null;
      if (this.qPos + 1 < this.queue.length) return this.queue[this.qPos + 1];
      return this.p.repeat === 'all' ? this.queue[0] : null;
    }

    advance(auto) {
      if (!this.queue.length || !this.queue.includes(this.cur)) this.buildQueue(this.cur >= 0 ? this.cur : null);
      if (!this.queue.length) return;
      let pos = this.qPos + 1;
      if (pos >= this.queue.length) {
        if (this.p.repeat === 'all' || !auto) {
          if (this.p.shuffle) { shuffleArr(this.queue); }
          pos = 0;
        } else {
          this.run = false; this.setRun();
          this.playCount = 0;
          this.setState('ended');
          this.setStatus('Playlist finished');
          return;
        }
      }
      this.qPos = pos;
      this.select(this.queue[pos], auto ? true : (this.run || this.state === 'playing'));
      if (!auto && this.run) { /* user skip keeps playing */ }
    }

    back() {
      if (this.audio.currentTime > 3 && this.state !== 'noaudio') { this.audio.currentTime = 0; return; }
      if (!this.queue.length || !this.queue.includes(this.cur)) this.buildQueue(this.cur >= 0 ? this.cur : null);
      if (!this.queue.length) return;
      let pos = this.qPos - 1;
      if (pos < 0) pos = this.p.repeat === 'all' ? this.queue.length - 1 : 0;
      this.qPos = pos;
      this.select(this.queue[pos], this.run || this.state === 'playing');
    }

    /* Browse words (Now Playing ‹ ›) — includes tracks without audio */
    browse(dir) {
      const order = this.visibleOrder();
      if (!order.length) return;
      let pos = order.indexOf(this.cur);
      pos = pos < 0 ? 0 : (pos + dir + order.length) % order.length;
      const i = order[pos];
      const wasPlaying = this.state === 'playing' || this.run;
      if (this.playable(i)) { this.buildQueue(i); this.select(i, wasPlaying); }
      else this.select(i, false);
    }

    /* ================= UI state ================= */
    setState(s) {
      this.state = s;
      this.dataset.state = s;
      const playing = s === 'playing' || s === 'waiting' || s === 'loading' || s === 'buffering';
      this.querySelectorAll('[data-action="toggle"]').forEach((b) => b.setAttribute('aria-label', playing ? 'Pause' : 'Play'));
      const pb = this.querySelector('.ayp-play');
      if (pb) pb.disabled = this.cur >= 0 ? !this.playable(this.cur) : !this.audioLangs.length;
      const row = this.rowById.get(this.cur);
      this.ui.rows.forEach((r) => r.classList.toggle('is-playing', r === row && playing));
      if (row) {
        const btn = row.querySelector('[data-play]');
        const t = this.tracks[this.cur];
        if (btn && t && this.playable(this.cur)) btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + this.primaryText(t));
      }
      if ('mediaSession' in navigator) { try { navigator.mediaSession.playbackState = playing ? 'playing' : (this.cur >= 0 ? 'paused' : 'none'); } catch (e) { /* noop */ } }
    }
    setRun() { this.dataset.run = this.run ? '1' : '0'; }
    setStatus(msg, isErr) {
      if (!this.ui.status) return;
      this.ui.status.textContent = msg || '';
      this.ui.status.classList.toggle('is-error', !!isErr);
    }
    primaryText(t) {
      const l = this.p.order.find((x) => !this.p.hidden.includes(x) && t[x]) || 'de';
      return t[l] || t.de || t.en || t.ar || '';
    }

    renderNowPlaying() {
      const t = this.tracks[this.cur];
      if (!t) return;
      this.setAttribute('data-has-current', '');
      const ui = this.ui;
      if (ui.npImg) {
        if (t.art) {
          ui.npImg.hidden = false;
          if (ui.npImg.getAttribute('src') !== t.art) { ui.npImg.src = t.art; }
          ui.npImg.alt = t.de || t.en || '';
          ui.npPh.hidden = true;
        } else { ui.npImg.hidden = true; ui.npImg.removeAttribute('src'); ui.npPh.hidden = false; }
      }
      if (ui.npWords) {
        LANGS.forEach((l) => {
          const el = ui.npWords.querySelector('.ayp-w[data-l="' + l + '"]');
          if (!el) return;
          el.innerHTML = l === 'de' ? articleHTML(t.de, this.cfg.articleColors) : escapeHTML(t[l] || '');
        });
      }
      const order = this.visibleOrder();
      const pos = order.indexOf(this.cur);
      if (ui.npPos) ui.npPos.textContent = (pos >= 0 ? pos + 1 : '–') + ' / ' + order.length;
      /* mini player */
      if (ui.mini) {
        ui.mini.hidden = false;
        ui.miniWord.innerHTML = '';
        const prim = this.p.order.find((x) => !this.p.hidden.includes(x) && t[x]) || 'de';
        const primEl = document.createElement('span');
        primEl.lang = prim; if (prim === 'ar') primEl.dir = 'rtl';
        primEl.innerHTML = prim === 'de' ? articleHTML(t.de, this.cfg.articleColors) : escapeHTML(t[prim] || '');
        ui.miniWord.appendChild(primEl);
        const subs = this.p.order.filter((x) => x !== prim && !this.p.hidden.includes(x) && t[x]).map((x) => t[x]);
        ui.miniSub.textContent = subs.join(' · ');
        if (t.img) { ui.miniImg.src = t.img; ui.miniImg.hidden = false; } else { ui.miniImg.hidden = true; }
      }
      /* favourites */
      this.querySelectorAll('[data-action="fav-current"]').forEach((b) => {
        b.disabled = false;
        const on = this.favs.has(t.id);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', (on ? 'Remove from favorites: ' : 'Add to favorites: ') + this.primaryText(t));
      });
      const pb = this.querySelector('.ayp-play');
      if (pb) pb.disabled = !this.playable(this.cur);
      if (ui.seek) ui.seek.disabled = !this.playable(this.cur);
      this.updateMediaSession();
    }

    markRows() {
      this.ui.rows.forEach((r) => {
        const on = +r.dataset.i === this.cur;
        r.classList.toggle('is-current', on);
        if (on) r.setAttribute('aria-current', 'true'); else r.removeAttribute('aria-current');
      });
      const row = this.rowById.get(this.cur);
      if (row && this._userScroll !== false) {
        const rect = row.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight - 100) {
          if (this.run) row.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }
    }

    resetProgress(disabled) {
      if (this.ui.seek) { this.ui.seek.value = 0; this.ui.seek.style.setProperty('--_pos', '0%'); this.ui.seek.style.setProperty('--_buf', '0%'); this.ui.seek.disabled = !!disabled; }
      if (this.ui.cur) this.ui.cur.textContent = '0:00';
      const t = this.tracks[this.cur];
      const d = t ? durCache[this.urlFor(t)] : null;
      if (this.ui.durTotal) this.ui.durTotal.textContent = disabled ? '–:––' : (d ? (this.showRemaining ? '-' + fmt(d) : fmt(d)) : '0:00');
      if (this.ui.miniBar) this.ui.miniBar.style.width = '0%';
    }

    updateProgress() {
      const a = this.audio, d = a.duration, ct = a.currentTime;
      if (!isFinite(d) || d <= 0) return;
      const pct = Math.min(100, (ct / d) * 100);
      if (this.ui.seek && !this._seeking) {
        this.ui.seek.value = Math.round(pct * 10);
        this.ui.seek.style.setProperty('--_pos', pct + '%');
        this.ui.seek.setAttribute('aria-valuetext', fmt(ct) + ' of ' + fmt(d) + ', ' + fmt(d - ct) + ' remaining');
      }
      if (this.ui.cur) this.ui.cur.textContent = fmt(ct);
      if (this.ui.durTotal) this.ui.durTotal.textContent = this.showRemaining ? '-' + fmt(d - ct) : fmt(d);
      if (this.ui.miniBar) this.ui.miniBar.style.width = pct + '%';
    }
    updateBuffered() {
      const a = this.audio;
      if (!this.ui.seek || !a.buffered.length || !isFinite(a.duration)) return;
      const end = a.buffered.end(a.buffered.length - 1);
      this.ui.seek.style.setProperty('--_buf', Math.min(100, (end / a.duration) * 100) + '%');
    }

    /* ================= languages ================= */
    applyLanguages() {
      const p = this.p;
      p.order.forEach((l, idx) => this.style.setProperty('--ayp-o-' + l, idx + 1));
      LANGS.forEach((l) => this.toggleAttribute('data-hide-' + l, p.hidden.includes(l)));
      const visible = p.order.filter((l) => !p.hidden.includes(l));
      this.dataset.primary = visible[0] || 'de';
      const cols = this.cfg.cols || {};
      const parts = [];
      if (cols.num) parts.push('var(--ayp-col-num)');
      if (cols.thumb) parts.push('var(--ayp-thumb)');
      visible.forEach((l, k) => parts.push(k === 0 ? 'minmax(0,1.25fr)' : 'minmax(0,1fr)'));
      if (cols.dur) parts.push('var(--ayp-col-dur)');
      if (cols.fav) parts.push('var(--ayp-col-btn)');
      parts.push('var(--ayp-col-btn)');
      this.style.setProperty('--ayp-cols', parts.join(' '));
      const chip = this.querySelector('[data-lang-chip] span');
      if (chip) chip.textContent = visible.map((l) => l.toUpperCase()).join(' · ');
      if (this.cur >= 0) this.renderNowPlaying();
    }

    renderAudioLangs() {
      const langs = this.audioLangs, labels = this.cfg.labels || {};
      const seg = this.ui.seg;
      if (seg) {
        seg.innerHTML = '';
        if (langs.length > 1) {
          langs.forEach((l) => {
            const b = document.createElement('button');
            b.type = 'button'; b.setAttribute('role', 'radio'); b.dataset.lang = l;
            b.textContent = l.toUpperCase();
            b.setAttribute('aria-label', labels[l] || l);
            seg.appendChild(b);
          });
          seg.hidden = false;
        } else seg.hidden = true;
      }
      const ch = this.ui.audioChoices;
      if (ch) {
        ch.innerHTML = '';
        if (!langs.length) {
          const p = document.createElement('p'); p.className = 'ayp-fs__hint'; p.textContent = this.cfg.msgs.noAudio;
          ch.appendChild(p);
        }
        LANGS.forEach((l) => {
          const has = langs.includes(l);
          if (!has && langs.length) return; // only offer languages with usable audio
          const lab = document.createElement('label');
          lab.className = 'ayp-choice';
          lab.innerHTML = '<input type="radio" name="' + this.id + '-alang" value="' + l + '"' + (has ? '' : ' disabled') + '><span class="ayp-choice__ui"><strong></strong><small></small></span>';
          lab.querySelector('strong').textContent = labels[l] || l;
          lab.querySelector('small').textContent = l.toUpperCase();
          ch.appendChild(lab);
        });
      }
      this.syncAudioLang();
    }
    syncAudioLang() {
      const l = this.p.audioLang;
      if (this.ui.seg) this.ui.seg.querySelectorAll('button').forEach((b) => {
        const on = b.dataset.lang === l;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      if (this.ui.audioChoices) this.ui.audioChoices.querySelectorAll('input').forEach((i) => { i.checked = i.value === l; });
      const lbl = this.querySelector('[data-audio-lang-label]');
      if (lbl && this.audioLangs.length) lbl.textContent = (lbl.dataset.base || (lbl.dataset.base = lbl.textContent)) + ' · ' + ((this.cfg.labels || {})[l] || l);
    }
    setAudioLang(l) {
      if (!this.audioLangs.includes(l) || l === this.p.audioLang) return;
      this.p.audioLang = l;
      this.savePrefs(['audioLang']);
      this.syncAudioLang();
      this.applyRowAvailability();
      this.updatePlayAllAvailability();
      if (this.cur >= 0) {
        const wasPlaying = this.state === 'playing' || this.state === 'waiting';
        this.audio.removeAttribute('src');
        this.select(this.cur, wasPlaying);
      }
    }

    renderOrderList() {
      const ol = this.ui.orderList;
      if (!ol) return;
      const labels = this.cfg.labels || {};
      ol.innerHTML = '';
      this.p.order.forEach((l, idx) => {
        const li = document.createElement('li');
        const off = this.p.hidden.includes(l);
        li.className = off ? 'is-off' : '';
        li.dataset.lang = l;
        li.innerHTML =
          '<span class="ayp-order__n">' + (idx + 1) + '</span>' +
          '<span class="ayp-order__name"></span>' +
          '<button type="button" class="ayp-order__vis" data-order="vis" aria-pressed="' + (!off) + '">' + (off ? 'Hidden' : 'Shown') + '</button>' +
          '<button type="button" class="ayp-icon-btn" data-order="up"' + (idx === 0 ? ' disabled' : '') + '>' + this.icon('arrow-up') + '</button>' +
          '<button type="button" class="ayp-icon-btn" data-order="down"' + (idx === this.p.order.length - 1 ? ' disabled' : '') + '>' + this.icon('arrow-down') + '</button>';
        const name = labels[l] || l;
        li.querySelector('.ayp-order__name').textContent = name;
        li.querySelector('[data-order="up"]').setAttribute('aria-label', 'Move ' + name + ' up');
        li.querySelector('[data-order="down"]').setAttribute('aria-label', 'Move ' + name + ' down');
        const vis = li.querySelector('[data-order="vis"]');
        vis.setAttribute('aria-label', (off ? 'Show ' : 'Hide ') + name);
        if (idx === 0) vis.disabled = true;
        ol.appendChild(li);
      });
    }
    icon(name) {
      const paths = { 'arrow-up': '<path d="M12 18.5v-13M6.5 11 12 5.5l5.5 5.5"/>', 'arrow-down': '<path d="M12 5.5v13M6.5 13l5.5 5.5 5.5-5.5"/>' };
      return '<svg class="ayp-ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">' + paths[name] + '</svg>';
    }
    moveLang(l, dir) {
      const o = this.p.order, i = o.indexOf(l), j = i + dir;
      if (i < 0 || j < 0 || j >= o.length) return;
      [o[i], o[j]] = [o[j], o[i]];
      if (this.p.hidden.includes(o[0])) this.p.hidden = this.p.hidden.filter((x) => x !== o[0]);
      this.afterLangChange(l, dir < 0 ? 'up' : 'down');
    }
    toggleLang(l) {
      if (this.p.order[0] === l) return;
      const h = new Set(this.p.hidden);
      h.has(l) ? h.delete(l) : h.add(l);
      this.p.hidden = Array.from(h);
      this.afterLangChange(l, 'vis');
    }
    afterLangChange(l, focusKind) {
      this.savePrefs(['order', 'hidden']);
      this.applyLanguages();
      this.renderOrderList();
      const btn = this.ui.orderList && this.ui.orderList.querySelector('li[data-lang="' + l + '"] [data-order="' + focusKind + '"]');
      if (btn && !btn.disabled) btn.focus();
      else if (this.ui.orderList) { const any = this.ui.orderList.querySelector('li[data-lang="' + l + '"] button:not(:disabled)'); if (any) any.focus(); }
    }

    /* ================= availability, favourites, durations ================= */
    applyRowAvailability() {
      const noAudio = this.cfg.msgs.noAudio;
      this.ui.rows.forEach((r) => {
        const i = +r.dataset.i, t = this.tracks[i];
        if (!t) return;
        const ok = this.playable(i);
        r.classList.toggle('is-noaudio', !ok);
        const btn = r.querySelector('[data-play]');
        const name = this.primaryText(t);
        if (btn) {
          btn.setAttribute('aria-label', ok ? 'Play ' + name : name + ' — ' + noAudio);
          if (ok) btn.removeAttribute('aria-disabled'); else btn.setAttribute('aria-disabled', 'true');
          btn.title = ok ? '' : noAudio;
        }
        const dur = r.querySelector('[data-dur]');
        if (dur && !ok) dur.innerHTML = '<span class="ayp-soon">' + escapeHTML(noAudio) + '</span>';
        else if (dur && ok && dur.querySelector('.ayp-soon')) dur.textContent = '';
      });
    }
    updatePlayAllAvailability() {
      const any = this.tracks.some((t, i) => this.playable(i));
      this.querySelectorAll('[data-action="play-all"], [data-action="shuffle-all"]').forEach((b) => { b.disabled = !any; });
    }

    applyFavs() {
      this.ui.rows.forEach((r) => {
        const t = this.tracks[+r.dataset.i];
        const b = r.querySelector('[data-fav]');
        if (!b || !t) return;
        const on = this.favs.has(t.id);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', (on ? 'Remove from favorites: ' : 'Add to favorites: ') + this.primaryText(t));
      });
      if (this.cur >= 0) this.renderNowPlaying();
      if (this.filterCat === 'fav') this.applyFilter();
    }
    toggleFav(i) {
      const t = this.tracks[i];
      if (!t || !this.cfg.favorites) return;
      this.favs.has(t.id) ? this.favs.delete(t.id) : this.favs.add(t.id);
      store.set(FAV_KEY + this.cfg.playlistId, Array.from(this.favs));
      this.applyFavs();
    }

    setRowDuration(i, d) {
      const r = this.rowById.get(i);
      const el = r && r.querySelector('[data-dur]');
      if (el && !el.querySelector('.ayp-soon')) el.textContent = fmt(d);
      this.updateTotal();
    }
    updateTotal() {
      if (!this.ui.total) return;
      let sum = 0, known = 0, need = 0;
      this.tracks.forEach((t, i) => {
        const u = this.urlFor(t);
        if (!u) return;
        need++;
        if (durCache[u] != null) { sum += durCache[u]; known++; }
      });
      if (need && known === need) { this.ui.totalText.textContent = fmtTotal(sum); this.ui.total.hidden = false; }
    }
    loadDurations() {
      const run = () => {
        const rows = this.ui.rows;
        if (!('IntersectionObserver' in window)) { rows.forEach((r) => this.probeRow(r)); return; }
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) { this._io.unobserve(e.target); this.probeRow(e.target); } });
        }, { rootMargin: '300px 0px' });
        rows.forEach((r) => this._io.observe(r));
        /* after the visible ones, quietly fill in the rest for the total */
        this.later(() => rows.forEach((r) => { if (!r._probed) this.probeRow(r); }), 4000);
      };
      ('requestIdleCallback' in window) ? requestIdleCallback(run, { timeout: 2500 }) : this.later(run, 600);
    }
    probeRow(r) {
      if (r._probed) return;
      r._probed = true;
      const i = +r.dataset.i, t = this.tracks[i];
      const url = this.urlFor(t);
      if (!url) return;
      probe(url, (d) => { if (this._ready && d != null) this.setRowDuration(i, d); });
    }

    /* ================= search & filter ================= */
    buildCategories() {
      const sel = this.ui.filter;
      if (!sel) return;
      if (this.cfg.categories) {
        const cats = [];
        this.tracks.forEach((t) => { const c = (t.cat || '').trim(); if (c && !cats.includes(c)) cats.push(c); });
        if (cats.length > 1) {
          const og = document.createElement('optgroup'); og.label = 'Category';
          cats.forEach((c) => { const o = document.createElement('option'); o.value = 'cat:' + c; o.textContent = c; og.appendChild(o); });
          sel.appendChild(og);
        }
      }
      if (sel.options.length < 2) sel.closest('.ayp-filter').hidden = true;
    }
    isFiltered(i) {
      const t = this.tracks[i];
      if (!t) return true;
      if (this.filterCat === 'fav' && !this.favs.has(t.id)) return true;
      if (this.filterCat.startsWith('cat:') && (t.cat || '').trim() !== this.filterCat.slice(4)) return true;
      if (this.filterText) {
        const terms = this.filterText.split(' ');
        if (!terms.every((q) => t._q.includes(q))) return true;
      }
      return false;
    }
    applyFilter() {
      let shown = 0;
      this.ui.rows.forEach((r) => {
        const hide = this.isFiltered(+r.dataset.i);
        r.classList.toggle('is-filtered', hide);
        if (!hide) shown++;
      });
      if (this.ui.noRes) {
        this.ui.noRes.hidden = shown > 0;
        this.ui.noRes.textContent = this.filterCat === 'fav' && !this.filterText ? 'No favorites yet — tap ♥ on a word to save it.' : this.cfg.msgs.noResults;
      }
      if (this.ui.clear) this.ui.clear.hidden = !this.ui.search.value;
      if (this.cur >= 0) this.renderNowPlaying();
      this._announce && clearTimeout(this._announce);
      this._announce = setTimeout(() => { if (this.filterText || this.filterCat !== 'all') this.setStatus(shown + (shown === 1 ? ' word' : ' words') + ' found'); }, 500);
    }

    /* ================= preferences UI ================= */
    syncPrefControls() {
      const p = this.p;
      this.querySelectorAll('[data-pref]').forEach((el) => {
        const k = el.dataset.pref;
        if (el.type === 'checkbox') el.checked = !!p[k];
        else el.value = String(p[k]);
      });
      this.querySelectorAll('[data-pref-radio]').forEach((el) => { el.checked = String(p[el.dataset.prefRadio]) === el.value; });
      const sh = this.querySelector('[data-action="shuffle"]');
      if (sh) { sh.setAttribute('aria-pressed', p.shuffle ? 'true' : 'false'); sh.setAttribute('aria-label', 'Shuffle ' + (p.shuffle ? 'on' : 'off')); }
      const rp = this.querySelector('[data-action="repeat"]');
      if (rp) {
        rp.dataset.repeat = p.repeat;
        rp.setAttribute('aria-label', 'Repeat: ' + ({ off: 'off', all: 'playlist', one: 'current word' })[p.repeat]);
      }
      if (this.ui.speedLbl) {
        this.ui.speedLbl.textContent = p.speed + '×';
        const b = this.ui.speedLbl.closest('button');
        if (b) b.setAttribute('aria-label', 'Playback speed ' + p.speed + '×. Change speed');
      }
      this.audio.playbackRate = p.speed;
    }
    setPref(k, v, fromEl) {
      const p = this.p;
      if (k === 'mode') {
        p.mode = v;
        const pre = MODE_PRESETS[v];
        if (pre) { p.plays = pre.plays; p.repGap = pre.repGap; p.trackGap = pre.trackGap; }
        if (v === this.defaults.mode) { p.plays = this.defaults.plays; p.repGap = this.defaults.repGap; p.trackGap = this.defaults.trackGap; }
        p.autoNext = true;
      } else if (k === 'speed') p.speed = parseFloat(v) || 1;
      else if (k === 'plays' || k === 'repGap' || k === 'trackGap') p[k] = +v;
      else if (k === 'remember') { p.remember = !!v; if (!v) store.set(PREF_KEY, { remember: false }); }
      else p[k] = v;
      this.sanitizePrefs();
      if (k === 'shuffle' && this.cur >= 0) this.buildQueue(this.cur);
      this.savePrefs(k === 'mode' ? ['mode', 'plays', 'repGap', 'trackGap', 'autoNext'] : [k]);
      this.syncPrefControls();
      if (fromEl && fromEl.focus && document.activeElement !== fromEl) { /* keep focus where it is */ }
    }
    resetPrefs() {
      store.del(PREF_KEY);
      this.touched = new Set();
      this.p = Object.assign({}, this.defaults, { order: this.defaults.order.slice(), hidden: this.defaults.hidden.slice() });
      this.sanitizePrefs();
      this.applyLanguages();
      this.renderOrderList();
      this.syncAudioLang();
      this.applyRowAvailability();
      this.syncPrefControls();
      this.setStatus('Preferences reset');
    }

    openSettings(section) {
      const d = this.ui.dialog;
      if (!d) return;
      this._returnFocus = document.activeElement;
      if (typeof d.showModal === 'function') { if (!d.open) d.showModal(); } else d.setAttribute('open', '');
      const target = section === 'learning' ? d.querySelector('[data-fs-learning] input:checked, [data-fs-learning] input') : d.querySelector('input:checked, select, button');
      if (target) { target.focus(); if (section === 'learning') target.closest('fieldset').scrollIntoView({ block: 'start' }); }
    }

    expand(open) {
      const isSheet = !window.matchMedia('(min-width: 1100px)').matches || this.dataset.layout === 'list';
      if (!isSheet) { if (open && this.ui.np) this.ui.np.focus(); return; }
      if (open) {
        this._returnFocus = document.activeElement;
        this.setAttribute('data-expanded', '');
        this.ui.np.setAttribute('role', 'dialog');
        this.ui.np.setAttribute('aria-modal', 'true');
        document.documentElement.classList.add('ayp-lock');
        document.documentElement.style.overflow = 'hidden';
        const btn = this.ui.np.querySelector('[data-action="collapse"]');
        setTimeout(() => btn && btn.focus(), 60);
      } else {
        this.removeAttribute('data-expanded');
        this.ui.np.removeAttribute('role');
        this.ui.np.removeAttribute('aria-modal');
        document.documentElement.classList.remove('ayp-lock');
        document.documentElement.style.overflow = '';
        const back = this._returnFocus && this._returnFocus.isConnected ? this._returnFocus : this.querySelector('.ayp-mini__open');
        if (back && back.focus) back.focus();
      }
    }

    /* ================= events ================= */
    bindUI() {
      const sig = { signal: this._ac.signal };
      this.addEventListener('click', (e) => this.onClick(e), sig);

      this.addEventListener('change', (e) => {
        const el = e.target;
        if (el.matches('[data-pref]')) this.setPref(el.dataset.pref, el.type === 'checkbox' ? el.checked : el.value, el);
        else if (el.matches('[data-pref-radio]')) this.setPref(el.dataset.prefRadio, el.value, el);
        else if (el.matches('[data-filter]')) { this.filterCat = el.value; this.applyFilter(); }
        else if (el.name === this.id + '-alang') this.setAudioLang(el.value);
      }, sig);

      if (this.ui.search) {
        let tmr;
        this.ui.search.addEventListener('input', () => {
          clearTimeout(tmr);
          tmr = setTimeout(() => { this.filterText = norm(this.ui.search.value); this.applyFilter(); }, 90);
        }, sig);
        this.ui.search.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.ui.search.value) { e.preventDefault(); this.clearSearch(); } }, sig);
      }

      if (this.ui.seek) {
        const seek = this.ui.seek;
        seek.addEventListener('input', () => {
          this._seeking = true;
          const d = this.audio.duration;
          seek.style.setProperty('--_pos', (seek.value / 10) + '%');
          if (isFinite(d)) { this.ui.cur.textContent = fmt((seek.value / 1000) * d); }
        }, sig);
        seek.addEventListener('change', () => {
          const d = this.audio.duration;
          if (isFinite(d)) this.audio.currentTime = (seek.value / 1000) * d;
          this._seeking = false;
        }, sig);
        seek.addEventListener('keydown', (e) => {
          const d = this.audio.duration;
          if (!isFinite(d)) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); this.audio.currentTime = Math.min(d, this.audio.currentTime + Math.max(1, d / 10)); }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); this.audio.currentTime = Math.max(0, this.audio.currentTime - Math.max(1, d / 10)); }
          if (e.key === 'Home') { e.preventDefault(); this.audio.currentTime = 0; }
          if (e.key === 'End') { e.preventDefault(); this.audio.currentTime = Math.max(0, d - 0.05); }
        }, sig);
      }

      if (this.ui.volume) {
        this.ui.volume.addEventListener('input', () => {
          this.audio.volume = this.ui.volume.value / 100;
          this.audio.muted = this.audio.volume === 0;
          const m = this.querySelector('[data-action="mute"]');
          if (m) m.setAttribute('aria-pressed', this.audio.muted ? 'true' : 'false');
        }, sig);
      }

      /* segmented control keyboard (radio group pattern) */
      if (this.ui.seg) {
        this.ui.seg.addEventListener('keydown', (e) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
          e.preventDefault();
          const l = this.audioLangs, i = l.indexOf(this.p.audioLang);
          const n = l[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + l.length) % l.length];
          this.setAudioLang(n);
          const b = this.ui.seg.querySelector('[data-lang="' + n + '"]'); if (b) b.focus();
        }, sig);
      }

      if (this.ui.dialog) {
        this.ui.dialog.addEventListener('close', () => {
          const r = this._returnFocus;
          if (r && r.isConnected && r.focus) r.focus();
        }, sig);
        this.ui.dialog.addEventListener('click', (e) => { if (e.target === this.ui.dialog) this.ui.dialog.close(); }, sig);
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('data-expanded') && !(this.ui.dialog && this.ui.dialog.open)) { e.preventDefault(); this.expand(false); }
        if (e.key === 'Tab' && this.hasAttribute('data-expanded') && !(this.ui.dialog && this.ui.dialog.open)) this.trapFocus(e);
      }, sig);

      const mq = window.matchMedia('(min-width: 1100px)');
      const onMq = () => { if (mq.matches && this.dataset.layout !== 'list' && this.hasAttribute('data-expanded')) this.expand(false); };
      mq.addEventListener ? mq.addEventListener('change', onMq, sig) : mq.addListener(onMq);

      /* Theme editor: pause when section is deselected/unloaded */
      document.addEventListener('shopify:section:unload', (e) => { if (e.target && e.target.contains && e.target.contains(this)) this.disconnectedCallback(); }, sig);
    }

    trapFocus(e) {
      const f = Array.from(this.ui.np.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')).filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!this.ui.np.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }

    clearSearch() {
      if (!this.ui.search) return;
      this.ui.search.value = '';
      this.filterText = '';
      this.applyFilter();
      this.ui.search.focus();
    }

    onClick(e) {
      const t = e.target;
      const actEl = t.closest('[data-action]');
      if (actEl && this.contains(actEl)) {
        const a = actEl.dataset.action;
        switch (a) {
          case 'play-all':
            if (this.run && (this.state === 'playing' || this.state === 'waiting' || this.state === 'loading' || this.state === 'buffering')) this.pause();
            else if (this.run && this.cur >= 0 && this.state === 'paused') this.toggle();
            else this.playAll(false);
            return;
          case 'shuffle-all': this.playAll(true); return;
          case 'toggle': this.toggle(); return;
          case 'next': this.advance(false); return;
          case 'prev': this.back(); return;
          case 'next-word': this.browse(1); return;
          case 'prev-word': this.browse(-1); return;
          case 'stop': this.stop(); return;
          case 'shuffle': this.setPref('shuffle', !this.p.shuffle); return;
          case 'repeat': this.setPref('repeat', ({ off: 'all', all: 'one', one: 'off' })[this.p.repeat]); return;
          case 'speed': {
            const i = SPEEDS.indexOf(+this.p.speed);
            this.setPref('speed', SPEEDS[(i + 1) % SPEEDS.length]);
            this.setStatus('Speed ' + this.p.speed + '×');
            return;
          }
          case 'mute': {
            this.audio.muted = !this.audio.muted;
            actEl.setAttribute('aria-pressed', this.audio.muted ? 'true' : 'false');
            actEl.setAttribute('aria-label', this.audio.muted ? 'Unmute' : 'Mute');
            return;
          }
          case 'fav-current': if (this.cur >= 0) this.toggleFav(this.cur); return;
          case 'toggle-remaining':
            this.showRemaining = !this.showRemaining;
            actEl.setAttribute('aria-label', this.showRemaining ? 'Show total time' : 'Show remaining time');
            this.updateProgress();
            return;
          case 'clear-search': this.clearSearch(); return;
          case 'open-settings': this.openSettings(); return;
          case 'open-learning': this.openSettings('learning'); return;
          case 'reset-prefs': this.resetPrefs(); return;
          case 'expand': this.expand(true); return;
          case 'collapse': this.expand(false); return;
        }
      }
      const seg = t.closest('[data-audio-langs] button[data-lang]');
      if (seg) { this.setAudioLang(seg.dataset.lang); return; }
      const ord = t.closest('[data-order]');
      if (ord) {
        const l = ord.closest('li').dataset.lang;
        if (ord.dataset.order === 'up') this.moveLang(l, -1);
        else if (ord.dataset.order === 'down') this.moveLang(l, 1);
        else this.toggleLang(l);
        return;
      }
      const fav = t.closest('[data-fav]');
      if (fav) { const r = fav.closest('.ayp-row'); if (r) this.toggleFav(+r.dataset.i); return; }
      const row = t.closest('.ayp-row');
      if (row && this.ui.list && this.ui.list.contains(row)) {
        const i = +row.dataset.i;
        this.playIndex(i);
        if (!this.playable(i)) {
          const isSheet = !window.matchMedia('(min-width: 1100px)').matches || this.dataset.layout === 'list';
          if (!isSheet && this.ui.np) { /* panel already visible on desktop */ }
        }
      }
    }

    /* ================= Media Session (lock screen / headset keys) ================= */
    updateMediaSession() {
      if (!('mediaSession' in navigator) || this.cur < 0) return;
      const t = this.tracks[this.cur];
      try {
        const art = t.art ? [{ src: t.art, sizes: '900x900', type: 'image/png' }] : [];
        navigator.mediaSession.metadata = new MediaMetadata({
          title: this.primaryText(t),
          artist: [t.en, t.ar].filter(Boolean).join(' · '),
          album: 'AYLINAS · ' + (this.cfg.title || ''),
          artwork: art
        });
        const ms = navigator.mediaSession;
        ms.setActionHandler('play', () => this.toggle());
        ms.setActionHandler('pause', () => this.pause());
        ms.setActionHandler('nexttrack', () => this.advance(false));
        ms.setActionHandler('previoustrack', () => this.back());
        try { ms.setActionHandler('stop', () => this.stop()); } catch (e) { /* unsupported */ }
      } catch (e) { /* MediaMetadata unsupported */ }
    }
  }

  customElements.define('aylinas-audio-playlist', AylinasPlaylist);
})();
